import React, { MouseEvent, useRef } from 'react';
import { useIntl, FormattedMessage } from '@kyruus/intl';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { PMCConfig } from '@kyruus/types';
import Theme from '@kyruus/ui-theme';
import { Button } from '@biome-design-system/ui/Button';
import { IconButton } from '@biome-design-system/ui/IconButton';
import { ChevronLeftIcon } from '@biome-design-system/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@biome-design-system/icons/ChevronRightIcon';
import { TuneIcon } from '@biome-design-system/icons/TuneIcon';

import type {
  GetUpdatedSearch,
  SearchFacet,
  SearchSummary,
  Log
} from 'Src/types';
import { FilterChips } from './FilterChips';
import { getUniqueAppliedFiltersCount, getAllFiltersMessage } from './utils';
import { messages as searchV9Messages } from '../utils';
import messages from './messages';
import { useHorizontalScroll } from './hooks/useHorizontalScroll';
import {
  ScrollLeft,
  ScrollRight,
  FilterChipWrapper,
  HorizontalFilterBar
} from './styles';
import { TRACKING_EVENTS } from './constants';

export interface FilterBarProps {
  disabled?: boolean;
  facets: SearchFacet[];
  getUpdatedSearch: GetUpdatedSearch;
  searchSummary: SearchSummary;
  config: PMCConfig;
  mapSearchUrl: string;
  onMapButtonClick: (e: MouseEvent) => void;
  log: Log;
  shouldShowMapLink: boolean;
  allFiltersButton: {
    onClick: (e: MouseEvent) => void;
    ariaHasPopup: 'dialog';
    ariaControls?: string;
    ariaExpanded: boolean;
  };
}

export const FilterBar = ({
  disabled,
  facets,
  config,
  getUpdatedSearch,
  searchSummary,
  mapSearchUrl,
  onMapButtonClick,
  log,
  shouldShowMapLink,
  allFiltersButton
}: FilterBarProps) => {
  const intl = useIntl();
  const filtersComponentRef = useRef<HTMLDivElement | null>(null);
  const {
    scrollLeft,
    scrollRight,
    canScrollLeft,
    canScrollRight,
    setScrollability
  } = useHorizontalScroll(filtersComponentRef, facets);
  const isScreenMediumOrBelow = useMediaQuery(
    `(max-width:${Theme.screen_medium})`
  );

  const locationChipCount = searchSummary.location ? 1 : 0;

  const countOfAppliedFilters =
    getUniqueAppliedFiltersCount(facets) + locationChipCount;

  const allFiltersLabel = getAllFiltersMessage(countOfAppliedFilters);

  const handleAllFiltersClick = (e: MouseEvent) => {
    allFiltersButton.onClick(e);
    log(TRACKING_EVENTS.ALL_FILTERS_OPEN_DRAWER);
  };

  const handleScrollLeft = () => {
    scrollLeft();
    log(TRACKING_EVENTS.SCROLL_LEFT);
  };
  const handleScrollRight = () => {
    scrollRight();
    log(TRACKING_EVENTS.SCROLL_RIGHT);
  };

  return (
    <HorizontalFilterBar
      role="toolbar"
      aria-orientation="horizontal"
      aria-label={intl.formatMessage(messages.ariaLabelAllFiltersToolbar)}
    >
      <Button
        aria-controls={
          allFiltersButton.ariaExpanded
            ? allFiltersButton.ariaControls
            : undefined
        }
        aria-expanded={allFiltersButton.ariaExpanded}
        aria-haspopup={allFiltersButton.ariaHasPopup}
        color="neutral"
        data-testid="all-filters-chip"
        disabled={disabled}
        endIcon={<TuneIcon />}
        onClick={handleAllFiltersClick}
        size="small"
        sx={{
          whiteSpace: 'nowrap',
          minWidth: 'fit-content',
          borderRadius: 16
        }}
        variant="outlined"
      >
        <FormattedMessage
          {...allFiltersLabel}
          values={{ filterCount: countOfAppliedFilters }}
        />
      </Button>
      {isScreenMediumOrBelow || disabled ? null : (
        <FilterChipWrapper
          role="group"
          ref={filtersComponentRef}
          onScrollCapture={setScrollability}
          aria-controls="provider-list"
          aria-label={intl.formatMessage(messages.ariaLabelSearchFilters)}
        >
          <ScrollLeft data-testid="scroll-filters-left" visible={canScrollLeft}>
            <IconButton
              aria-label={intl.formatMessage(messages.ariaScrollLeft)}
              onClick={handleScrollLeft}
              tabIndex={canScrollLeft ? undefined : -1}
              variant="outlined"
              size="small"
              sx={{ borderRadius: 16 }}
            >
              <ChevronLeftIcon />
            </IconButton>
          </ScrollLeft>
          <FilterChips
            facets={facets}
            getUpdatedSearch={getUpdatedSearch}
            searchSummary={searchSummary}
            config={config}
            log={log}
          />
          <ScrollRight
            data-testid="scroll-filters-right"
            visible={canScrollRight}
          >
            <IconButton
              aria-label={intl.formatMessage(messages.ariaScrollRight)}
              onClick={handleScrollRight}
              tabIndex={canScrollRight ? undefined : -1}
              variant="outlined"
              size="small"
              sx={{ borderRadius: 16 }}
            >
              <ChevronRightIcon />
            </IconButton>
          </ScrollRight>
        </FilterChipWrapper>
      )}
      {shouldShowMapLink && (
        <Button
          aria-label={intl.formatMessage(
            searchV9Messages.mapSearchLinkAriaLabel
          )}
          color="neutral"
          data-testid="MapSearchLink"
          href={mapSearchUrl}
          onClick={onMapButtonClick}
          size="small"
          sx={{ borderRadius: 16 }}
          variant="outlined"
        >
          <FormattedMessage {...searchV9Messages.mapSearchLinkText} />
        </Button>
      )}
    </HorizontalFilterBar>
  );
};
